import { FormEvent, useEffect, useMemo, useState } from "react";
import { PASSWORD_MIN_LENGTH, type AdminOverview, type DataScope, type ManagedUser, type PermissionItem, type PermissionTemplate, type PermissionTemplateInput, type RegistrationRequestItem } from "@cyh/contracts";
import {
  ClipboardCheck,
  Copy,
  Eye,
  EyeOff,
  KeyRound,
  LayoutTemplate,
  Pencil,
  Plus,
  RotateCcwKey,
  Search,
  Trash2,
  UserRoundCheck,
  UsersRound,
  X
} from "lucide-react";
import { api, type AuditListItem, type RecordPage } from "../api";
import { useLanguage } from "../i18n";
import { dateBounds, EMPTY_DATE_FILTER, matchesRecordDate, pageSlice, RecordDateFilter, RecordPagination, type DateFilterValue } from "./RecordListControls";

type DirectDataScope = Exclude<DataScope, "DEPARTMENT">;
type Tab = "users" | "templates" | "registrations" | "audit";
type DialogKind = "create-user" | "edit-user" | "assign-permissions" | "reset-password" | "review-registration" | "create-template" | "edit-template";
type RegistrationPage = RecordPage<RegistrationRequestItem> & { pendingCount: number };

const EMPTY_REGISTRATION_PAGE: RegistrationPage = { items: [], total: 0, page: 1, pageSize: 20, pageCount: 1, pendingCount: 0 };
const EMPTY_AUDIT_PAGE: RecordPage<AuditListItem> = { items: [], total: 0, page: 1, pageSize: 20, pageCount: 1 };

const scopeLabels: Record<DirectDataScope, [string, string]> = {
  ALL: ["全部数据", "All data"],
  SELF: ["仅本人数据", "Own data only"],
  ASSIGNED: ["分配给我的数据", "Assigned data"]
};
const scopeDescriptions: Record<DirectDataScope, [string, string]> = {
  SELF: ["仅查看和操作由本人创建或本人负责的数据；不会看到其他同事的数据。", "Only records created by or owned by this account; other employees’ records are hidden."],
  ASSIGNED: ["查看明确分配给本人参与的数据；尚未支持参与人分配的模块暂按本人数据处理。", "Records explicitly assigned to this account; modules without assignment support currently fall back to own data."],
  ALL: ["查看公司范围内的数据，但仍只能使用已勾选的功能权限，不代表系统管理员权限。", "Company-wide records, still limited to selected functional permissions; this is not system-administrator access."]
};

const actionLabels: Record<string, [string, string]> = {
  AUTH_LOGIN: ["账号登录", "Account login"],
  AUTH_LOGOUT: ["退出登录", "Logout"],
  AUTH_PASSWORD_CHANGE: ["修改本人密码", "Change own password"],
  IDENTITY_USER_CREATE: ["创建账号", "Create account"],
  IDENTITY_USER_UPDATE: ["编辑员工资料", "Edit employee"],
  IDENTITY_STATUS_CHANGE: ["变更账号状态", "Change account status"],
  IDENTITY_PASSWORD_RESET: ["重置员工密码", "Reset employee password"],
  IDENTITY_USER_PERMISSIONS_UPDATE: ["调整账号权限", "Update account permissions"],
  IDENTITY_PERMISSION_TEMPLATE_CREATE: ["创建权限模板", "Create permission template"],
  IDENTITY_PERMISSION_TEMPLATE_UPDATE: ["修改权限模板", "Update permission template"],
  IDENTITY_PERMISSION_TEMPLATE_DELETE: ["删除权限模板", "Delete permission template"],
  IDENTITY_USER_DELETE: ["删除已停用账号", "Delete disabled account"],
  IDENTITY_REGISTRATION_SUBMIT: ["提交注册申请", "Submit registration"],
  IDENTITY_REGISTRATION_APPROVE: ["批准注册申请", "Approve registration"],
  IDENTITY_REGISTRATION_REJECT: ["拒绝注册申请", "Reject registration"]
};

export function AdminPanel({ permissions, currentUserId }: { permissions: string[]; currentUserId: string }) {
  const { language, t } = useLanguage();
  const [overview, setOverview] = useState<AdminOverview | null>(null);
  const [registrations, setRegistrations] = useState<RegistrationPage>(EMPTY_REGISTRATION_PAGE);
  const [audit, setAudit] = useState<RecordPage<AuditListItem>>(EMPTY_AUDIT_PAGE);
  const [tab, setTab] = useState<Tab>("users");
  const [dialog, setDialog] = useState<DialogKind | null>(null);
  const [selectedUser, setSelectedUser] = useState<ManagedUser | null>(null);
  const [selectedRegistration, setSelectedRegistration] = useState<RegistrationRequestItem | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<PermissionTemplate | null>(null);
  const [query, setQuery] = useState("");
  const [dateFilter, setDateFilter] = useState<DateFilterValue>(EMPTY_DATE_FILTER);
  const [page, setPage] = useState(1);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const allowed = (code: string) => permissions.includes("*") || permissions.includes(code);

  const refresh = async () => {
    try {
      setOverview(await api.adminOverview());
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t("加载账号失败", "Failed to load accounts"));
    }
  };
  const refreshRegistrations = async () => {
    if (!allowed("identity.registration.read")) return;
    try {
      const bounds = dateBounds(dateFilter);
      setRegistrations(await api.registrationRequests({ page, pageSize: 20, dateFrom: bounds.from, dateTo: bounds.to }));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t("加载注册申请失败", "Failed to load registrations"));
    }
  };
  const refreshAudit = async () => {
    if (!allowed("audit.read")) return;
    try {
      const bounds = dateBounds(dateFilter);
      setAudit(await api.audit({ page, pageSize: 20, dateFrom: bounds.from, dateTo: bounds.to }));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t("加载审计记录失败", "Failed to load audit log"));
    }
  };

  useEffect(() => {
    void refresh();
    void refreshRegistrations();
  }, []);

  useEffect(() => {
    if (tab === "registrations") void refreshRegistrations();
    if (tab === "audit") void refreshAudit();
  }, [tab, page, dateFilter]);

  const users = useMemo(() => {
    const keyword = query.toLowerCase();
    return (overview?.users ?? []).filter((user) =>
      `${user.name}${user.username}${user.email}${user.permissions.join("")}`.toLowerCase().includes(keyword) && matchesRecordDate(user.createdAt, dateFilter)
    );
  }, [overview, query, dateFilter]);
  const pagedUsers = pageSlice(users, page, 20);
  const visibleUsers = tab === "users" ? pagedUsers.items : [];
  const visibleRegistrations = tab === "registrations" ? registrations.items : [];
  const visibleAudit = tab === "audit" ? audit.items : [];
  useEffect(() => setPage(1), [tab, query, dateFilter]);

  const openUserDialog = (kind: DialogKind, user: ManagedUser) => {
    setSelectedUser(user);
    setDialog(kind);
    setError("");
  };
  const saved = async () => {
    setDialog(null);
    await refresh();
  };

  async function changeStatus(user: ManagedUser) {
    const next = user.status === "ACTIVE" ? "DISABLED" : "ACTIVE";
    const message = next === "DISABLED"
      ? t(`确定停用账号“${user.name}”吗？该账号会立即退出登录。`, `Disable “${user.name}”? All sessions will end immediately.`)
      : t(`确定重新启用账号“${user.name}”吗？`, `Re-enable “${user.name}”?`);
    if (!window.confirm(message)) return;
    setBusy(true);
    setError("");
    try {
      await api.setUserStatus(user.id, next);
      await refresh();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t("状态变更失败", "Status update failed"));
    } finally {
      setBusy(false);
    }
  }

  async function deleteUser(user: ManagedUser) {
    if (!window.confirm(t(
      `确定删除已停用账号“${user.name}”吗？账号将不能恢复，但历史订单和审计记录会保留。`,
      `Delete disabled account “${user.name}”? The account cannot be restored, while business and audit history will remain.`
    ))) return;
    setBusy(true);
    setError("");
    try {
      await api.deleteUser(user.id);
      await refresh();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t("删除账号失败", "Failed to delete account"));
    } finally {
      setBusy(false);
    }
  }

  async function deleteTemplate(template: PermissionTemplate) {
    if (!window.confirm(t(`确定删除自定义权限模板“${template.name}”吗？已经使用该模板分配的账号权限不会改变。`, `Delete custom permission template “${template.name}”? Accounts previously configured from it will not change.`))) return;
    setBusy(true); setError("");
    try { await api.deletePermissionTemplate(template.id, template.version); await refresh(); }
    catch (cause) { setError(cause instanceof Error ? cause.message : t("删除模板失败", "Failed to delete template")); }
    finally { setBusy(false); }
  }

  const permissionName = (code: string) => overview?.permissions.find((item) => item.code === code)?.name ?? code;
  const pendingCount = registrations.pendingCount;

  return <div className="admin-page">
    <div className="admin-title">
      <div>
        <span className="eyebrow">SYSTEM ADMINISTRATION</span>
        <h2>{t("账号与权限", "Accounts & Access")}</h2>
        <p>{t("权限与数据范围直接分配到每个账号，不再使用部门和角色。", "Permissions and data scope are assigned directly to each account, without departments or roles.")}</p>
      </div>
      {allowed("identity.user.create") && <button className="button primary" onClick={() => setDialog("create-user")}>
        <Plus size={16} />{t("新建员工账号", "New employee account")}
      </button>}
    </div>

    {error && <div className="admin-error">{error}<button onClick={() => setError("")} aria-label={t("关闭", "Close")}><X /></button></div>}

    <div className="admin-metrics">
      <div><UsersRound /><span>{t("员工账号", "Employee accounts")}<strong>{overview?.users.length ?? 0}</strong></span></div>
      <div><UserRoundCheck /><span>{t("正常账号", "Active accounts")}<strong>{overview?.users.filter((user) => user.status === "ACTIVE").length ?? 0}</strong></span></div>
      <div><ClipboardCheck /><span>{t("待审核注册", "Pending registrations")}<strong>{pendingCount}</strong></span></div>
      <div><KeyRound /><span>{t("可分配权限", "Assignable permissions")}<strong>{overview?.permissions.length ?? 0}</strong></span></div>
    </div>

    <div className="admin-tabs" role="tablist">
      <button className={tab === "users" ? "active" : ""} onClick={() => setTab("users")}>{t("员工账号", "Employees")}</button>
      {allowed("identity.user.permissions") && <button className={tab === "templates" ? "active" : ""} onClick={() => setTab("templates")}>{t("权限模板", "Permission templates")}</button>}
      {allowed("identity.registration.read") && <button className={tab === "registrations" ? "active" : ""} onClick={() => setTab("registrations")}>
        {t("注册审核", "Registration review")}{pendingCount ? ` (${pendingCount})` : ""}
      </button>}
      {allowed("audit.read") && <button className={tab === "audit" ? "active" : ""} onClick={() => setTab("audit")}>{t("操作审计", "Audit log")}</button>}
    </div>

    {tab === "users" && <section className="admin-section">
      <div className="admin-toolbar">
        <label><Search size={16} /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder={t("搜索姓名、账号、邮箱或权限", "Search name, account, email, or permission")} /></label>
        <RecordDateFilter value={dateFilter} onChange={setDateFilter} />
        <span>{t(`共 ${users.length} 个账号`, `${users.length} account(s)`)}</span>
      </div>
      <div className="admin-table-wrap"><table className="admin-table">
        <thead><tr><th>{t("员工", "Employee")}</th><th>{t("账号权限", "Account permissions")}</th><th>{t("数据范围", "Data scope")}</th><th>{t("状态", "Status")}</th><th>{t("最后登录", "Last login")}</th><th aria-label={t("操作", "Actions")} /></tr></thead>
        <tbody>{visibleUsers.length ? visibleUsers.map((user) => <tr key={user.id}>
          <td><div className="user-cell"><span>{user.name.slice(0, 1)}</span><div><strong>{user.name}</strong><small>{user.username} · {user.email}</small></div></div></td>
          <td><div className="role-tags">{user.permissions.slice(0, 3).map((code) => <span key={code}>{permissionName(code)}</span>)}{user.permissions.length > 3 && <span>+{user.permissions.length - 3}</span>}</div></td>
          <td>{t(scopeLabels[user.dataScope][0], scopeLabels[user.dataScope][1])}</td>
          <td><span className={`account-status ${user.status.toLowerCase()}`}>{user.status === "ACTIVE" ? t("正常", "Active") : t("已停用", "Disabled")}{user.mustChangePassword ? t(" · 待改密", " · Password change required") : ""}</span></td>
          <td>{user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleString(language, { month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" }) : t("从未登录", "Never")}</td>
          <td><div className="row-actions">
            {allowed("identity.user.update") && <button onClick={() => openUserDialog("edit-user", user)}>{t("编辑资料", "Edit")}</button>}
            {allowed("identity.user.permissions") && user.id !== currentUserId && <button onClick={() => openUserDialog("assign-permissions", user)}>{t("分配权限", "Permissions")}</button>}
            {allowed("identity.user.reset_password") && user.id !== currentUserId && <button onClick={() => openUserDialog("reset-password", user)}>{t("重置密码", "Reset password")}</button>}
            {allowed("identity.user.disable") && <button disabled={busy || user.id === currentUserId} className={user.status === "ACTIVE" ? "danger" : ""} onClick={() => void changeStatus(user)}>{user.status === "ACTIVE" ? t("停用", "Disable") : t("启用", "Enable")}</button>}
            {allowed("identity.user.delete") && user.status === "DISABLED" && user.id !== currentUserId && <button disabled={busy} className="danger" onClick={() => void deleteUser(user)}><Trash2 size={13} />{t("删除", "Delete")}</button>}
          </div></td>
        </tr>) : <tr><td colSpan={6} className="empty-cell">{t("没有匹配的账号", "No matching accounts")}</td></tr>}</tbody>
      </table></div>
      <RecordPagination page={pagedUsers.page} pageCount={pagedUsers.pageCount} total={users.length} onPageChange={setPage} />
    </section>}

    {tab === "templates" && overview && <section className="admin-section">
      <div className="admin-toolbar template-toolbar"><span>{t("模板用于快速预填权限和数据范围，保存后仍是账号直接权限，不会与模板持续绑定。", "Templates prefill permissions and data scope. Saved accounts keep direct access and are not continuously linked to a template.")}</span><button className="button primary" onClick={() => { setSelectedTemplate(null); setDialog("create-template"); }}><Plus />{t("新建自定义模板", "New custom template")}</button></div>
      <div className="permission-template-grid">{overview.permissionTemplates.map((template) => <article key={template.id} className={template.system ? "system" : "custom"}><div className="template-card-heading"><span><LayoutTemplate /></span><div><strong>{t(template.name, template.nameEn || template.name)}</strong><small>{template.system ? t("系统预设模板 · 可编辑", "System preset · Editable") : t("自定义模板", "Custom template")}</small></div></div><p>{t(template.description, template.descriptionEn || template.description)}</p><div className="template-card-meta"><span>{t(scopeLabels[template.dataScope][0], scopeLabels[template.dataScope][1])}</span><b>{t(`${template.permissions.length} 项权限`, `${template.permissions.length} permissions`)}</b></div><div className="template-permission-preview">{template.permissions.slice(0, 5).map((code) => <span key={code}>{permissionName(code)}</span>)}{template.permissions.length > 5 && <span>+{template.permissions.length - 5}</span>}</div><div className="template-card-actions"><button onClick={() => { setSelectedTemplate(template); setDialog("create-template"); }}><Copy />{t("复制新建", "Duplicate")}</button><button onClick={() => { setSelectedTemplate(template); setDialog("edit-template"); }}><Pencil />{t("编辑", "Edit")}</button>{!template.system && <button className="danger" disabled={busy} onClick={() => void deleteTemplate(template)}><Trash2 />{t("删除", "Delete")}</button>}</div></article>)}</div>
    </section>}

    {tab === "registrations" && <section className="admin-section">
      <div className="admin-toolbar"><RecordDateFilter value={dateFilter} onChange={setDateFilter} /><span><ClipboardCheck size={16} /> {t(`共 ${registrations.total} 条注册申请`, `${registrations.total} registration request(s)`)}</span></div>
      <div className="admin-table-wrap"><table className="admin-table">
        <thead><tr><th>{t("申请人", "Applicant")}</th><th>{t("联系方式", "Contact")}</th><th>{t("申请时间", "Submitted")}</th><th>{t("审核状态", "Status")}</th><th>{t("授权结果", "Access result")}</th><th aria-label={t("操作", "Actions")} /></tr></thead>
        <tbody>{visibleRegistrations.length ? visibleRegistrations.map((item) => <tr key={item.id}>
          <td><strong>{item.name}</strong><small>{item.username}</small></td>
          <td><strong>{item.email}</strong><small>{item.mobile || t("未填写手机", "No mobile")}</small></td>
          <td>{new Date(item.createdAt).toLocaleString(language)}</td>
          <td><span className={`registration-status ${item.status.toLowerCase()}`}>{item.status === "PENDING" ? t("待审核", "Pending") : item.status === "APPROVED" ? t("已通过", "Approved") : t("已拒绝", "Rejected")}</span></td>
          <td>{item.status === "APPROVED" && item.dataScope ? t(`${item.permissions.length} 项权限 · ${scopeLabels[item.dataScope][0]}`, `${item.permissions.length} permission(s) · ${scopeLabels[item.dataScope][1]}`) : item.reviewReason || "—"}</td>
          <td><div className="row-actions">{item.status === "PENDING" && allowed("identity.registration.review") && <button onClick={() => { setSelectedRegistration(item); setDialog("review-registration"); }}>{t("审核并授权", "Review & grant")}</button>}</div></td>
        </tr>) : <tr><td colSpan={6} className="empty-cell">{t("暂无注册申请", "No registration requests")}</td></tr>}</tbody>
      </table></div>
      <RecordPagination page={registrations.page} pageCount={registrations.pageCount} total={registrations.total} onPageChange={setPage} />
    </section>}

    {tab === "audit" && <section className="admin-section"><div className="admin-toolbar"><RecordDateFilter value={dateFilter} onChange={setDateFilter} /><span>{t(`共 ${audit.total} 条操作记录`, `${audit.total} audit record(s)`)}</span></div><div className="admin-table-wrap"><table className="admin-table">
      <thead><tr><th>{t("操作时间", "Time")}</th><th>{t("操作人", "Actor")}</th><th>{t("动作", "Action")}</th><th>{t("对象", "Resource")}</th><th>{t("审计编号", "Audit ID")}</th></tr></thead>
      <tbody>{visibleAudit.length ? visibleAudit.map((item) => <tr key={item.id}><td>{new Date(item.occurredAt).toLocaleString(language)}</td><td>{item.actorName}</td><td>{actionLabels[item.action] ? t(actionLabels[item.action]![0], actionLabels[item.action]![1]) : item.action}</td><td>{item.resourceType} · {item.resourceId}</td><td><code>{item.id.slice(0, 8)}</code></td></tr>) : <tr><td colSpan={5} className="empty-cell">{t("暂无管理操作记录", "No administrative audit records")}</td></tr>}</tbody>
    </table></div><RecordPagination page={audit.page} pageCount={audit.pageCount} total={audit.total} onPageChange={setPage} /></section>}

    {dialog === "create-user" && overview && <CreateUserDialog overview={overview} onClose={() => setDialog(null)} onSaved={saved} onError={setError} />}
    {dialog === "edit-user" && selectedUser && <EditUserDialog user={selectedUser} onClose={() => setDialog(null)} onSaved={saved} onError={setError} />}
    {dialog === "assign-permissions" && overview && selectedUser && <AssignPermissionsDialog user={selectedUser} overview={overview} onClose={() => setDialog(null)} onSaved={saved} onError={setError} />}
    {dialog === "reset-password" && selectedUser && <ResetPasswordDialog user={selectedUser} onClose={() => setDialog(null)} onSaved={saved} onError={setError} />}
    {dialog === "review-registration" && overview && selectedRegistration && <ReviewRegistrationDialog request={selectedRegistration} overview={overview} onClose={() => setDialog(null)} onSaved={async () => { setDialog(null); await refreshRegistrations(); await refresh(); }} onError={setError} />}
    {dialog === "create-template" && overview && <PermissionTemplateDialog source={selectedTemplate} overview={overview} mode="create" onClose={() => setDialog(null)} onSaved={saved} onError={setError} />}
    {dialog === "edit-template" && overview && selectedTemplate && <PermissionTemplateDialog source={selectedTemplate} overview={overview} mode="edit" onClose={() => setDialog(null)} onSaved={saved} onError={setError} />}
  </div>;
}

function Dialog({ title, children, onClose }: { title: string; children: React.ReactNode; onClose: () => void }) {
  const { t } = useLanguage();
  return <div className="modal-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
    <div className="admin-modal" role="dialog" aria-modal="true" aria-label={title}>
      <div className="modal-heading"><h3>{title}</h3><button onClick={onClose} aria-label={t("关闭", "Close")}><X /></button></div>
      {children}
    </div>
  </div>;
}

function PasswordField() {
  const { t } = useLanguage();
  const [visible, setVisible] = useState(false);
  return <label className="full-field">{t("临时密码", "Temporary password")}
    <div className="password-input admin-password-input">
      <input name="temporaryPassword" type={visible ? "text" : "password"} autoComplete="new-password" required minLength={PASSWORD_MIN_LENGTH} placeholder={t("至少 8 位，包含大小写字母和数字", "At least 8 characters with upper/lowercase letters and a number")} pattern={`(?=.*[a-z])(?=.*[A-Z])(?=.*\\d).{${PASSWORD_MIN_LENGTH},}`} />
      <button type="button" aria-label={visible ? t("隐藏临时密码", "Hide temporary password") : t("显示临时密码", "Show temporary password")} onClick={() => setVisible((value) => !value)}>{visible ? <EyeOff /> : <Eye />}</button>
    </div>
    <small>{t("请通过安全渠道交给员工；首次登录后必须修改。", "Share it securely. The employee must change it on first login.")}</small>
  </label>;
}

function PermissionEditor({ catalog, templates, selected, scope, onSelected, onScope }: {
  catalog: PermissionItem[];
  templates?: PermissionTemplate[];
  selected: string[];
  scope: DirectDataScope;
  onSelected: (value: string[]) => void;
  onScope: (value: DirectDataScope) => void;
}) {
  const { t } = useLanguage();
  const groups = [...new Set(catalog.map((item) => item.group))];
  const toggle = (code: string, checked: boolean) => onSelected(checked ? [...new Set([...selected, code])] : selected.filter((item) => item !== code));
  const applyTemplate = (id: string) => {
    const template = templates?.find((item) => item.id === id);
    if (!template) return;
    onSelected(template.permissions);
    onScope(template.dataScope);
  };
  return <>
    {templates?.length ? <label className="template-picker">{t("从权限模板快速填入", "Prefill from permission template")}<select defaultValue="" onChange={(event) => applyTemplate(event.target.value)}><option value="" disabled>{t("选择模板…", "Select a template…")}</option>{templates.map((template) => <option value={template.id} key={template.id}>{t(template.name, template.nameEn || template.name)} · {t(scopeLabels[template.dataScope][0], scopeLabels[template.dataScope][1])}</option>)}</select><small>{t("选择模板会替换下方当前勾选内容；您仍可继续增减权限。", "Selecting a template replaces the current checks; you can still adjust individual permissions.")}</small></label> : null}
    <label className="scope-field">{t("数据范围", "Data scope")}
      <select value={scope} onChange={(event) => onScope(event.target.value as DirectDataScope)}>
        {(Object.keys(scopeLabels) as DirectDataScope[]).map((value) => <option value={value} key={value}>{t(scopeLabels[value][0], scopeLabels[value][1])}</option>)}
      </select>
      <small>{t(scopeDescriptions[scope][0], scopeDescriptions[scope][1])}</small>
    </label>
    <div className="scope-explanation">{(Object.keys(scopeLabels) as DirectDataScope[]).map((value) => <button type="button" key={value} className={scope === value ? "active" : ""} onClick={() => onScope(value)}><strong>{t(scopeLabels[value][0], scopeLabels[value][1])}</strong><span>{t(scopeDescriptions[value][0], scopeDescriptions[value][1])}</span></button>)}</div>
    <div className="permission-editor">
      {groups.map((group) => <div key={group}>
        <strong>{group}</strong>
        {catalog.filter((item) => item.group === group).map((permission) => <label key={permission.code}>
          <input type="checkbox" checked={selected.includes(permission.code)} onChange={(event) => toggle(permission.code, event.target.checked)} />
          <span><b>{permission.name}<em className={`permission-risk ${permission.risk}`}>{t(({ read: "读取", write: "操作", approve: "审批/确认", admin: "管理" } as const)[permission.risk], permission.risk)}</em></b><small className="permission-description">{t(permission.description, permission.descriptionEn || permission.description)}</small><small>{permission.code}</small></span>
        </label>)}
      </div>)}
    </div>
  </>;
}

function CreateUserDialog({ overview, onClose, onSaved, onError }: { overview: AdminOverview; onClose: () => void; onSaved: () => Promise<void>; onError: (value: string) => void }) {
  const { t } = useLanguage();
  const [selected, setSelected] = useState<string[]>([]);
  const [scope, setScope] = useState<DirectDataScope>("SELF");
  const [busy, setBusy] = useState(false);
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!selected.length) return onError(t("请至少选择一项权限", "Select at least one permission"));
    setBusy(true);
    const data = new FormData(event.currentTarget);
    try {
      await api.createUser({ name: String(data.get("name")), username: String(data.get("username")), email: String(data.get("email")), mobile: String(data.get("mobile")), permissions: selected, dataScope: scope, temporaryPassword: String(data.get("temporaryPassword")) });
      await onSaved();
    } catch (cause) {
      onError(cause instanceof Error ? cause.message : t("创建失败", "Create failed"));
    } finally {
      setBusy(false);
    }
  }
  return <Dialog title={t("新建员工账号", "New employee account")} onClose={onClose}>
    <form className="admin-form" onSubmit={submit}>
      <label>{t("员工姓名", "Employee name")}<input name="name" required minLength={2} /></label>
      <label>{t("登录账号", "Username")}<input name="username" required pattern="[a-zA-Z][a-zA-Z0-9._-]{2,31}" /></label>
      <label>{t("工作邮箱", "Business email")}<input name="email" type="email" required /></label>
      <label>{t("手机号码", "Mobile")}<input name="mobile" /></label>
      <PasswordField />
      <div className="full-field"><PermissionEditor catalog={overview.permissions} templates={overview.permissionTemplates} selected={selected} scope={scope} onSelected={setSelected} onScope={setScope} /></div>
      <Actions busy={busy} disabled={!selected.length} onClose={onClose} text={t("创建账号", "Create account")} />
    </form>
  </Dialog>;
}

function EditUserDialog({ user, onClose, onSaved, onError }: { user: ManagedUser; onClose: () => void; onSaved: () => Promise<void>; onError: (value: string) => void }) {
  const { t } = useLanguage();
  const [busy, setBusy] = useState(false);
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    const data = new FormData(event.currentTarget);
    try {
      await api.updateUser(user.id, { name: String(data.get("name")), username: String(data.get("username")), email: String(data.get("email")), mobile: String(data.get("mobile")) });
      await onSaved();
    } catch (cause) {
      onError(cause instanceof Error ? cause.message : t("保存失败", "Save failed"));
    } finally {
      setBusy(false);
    }
  }
  return <Dialog title={t(`编辑员工 · ${user.name}`, `Edit employee · ${user.name}`)} onClose={onClose}>
    <form className="admin-form" onSubmit={submit}>
      <label>{t("员工姓名", "Employee name")}<input name="name" defaultValue={user.name} required minLength={2} /></label>
      <label>{t("登录账号", "Username")}<input name="username" defaultValue={user.username} required pattern="[a-zA-Z][a-zA-Z0-9._-]{2,31}" /></label>
      <label>{t("工作邮箱", "Business email")}<input name="email" type="email" defaultValue={user.email} required /></label>
      <label>{t("手机号码", "Mobile")}<input name="mobile" defaultValue={user.mobile} /></label>
      <Actions busy={busy} onClose={onClose} text={t("保存资料", "Save details")} />
    </form>
  </Dialog>;
}

function AssignPermissionsDialog({ user, overview, onClose, onSaved, onError }: { user: ManagedUser; overview: AdminOverview; onClose: () => void; onSaved: () => Promise<void>; onError: (value: string) => void }) {
  const { t } = useLanguage();
  const [selected, setSelected] = useState(user.permissions);
  const [scope, setScope] = useState<DirectDataScope>(user.dataScope);
  const [busy, setBusy] = useState(false);
  async function save() {
    setBusy(true);
    try {
      await api.assignPermissions(user.id, selected, scope);
      await onSaved();
    } catch (cause) {
      onError(cause instanceof Error ? cause.message : t("保存失败", "Save failed"));
    } finally {
      setBusy(false);
    }
  }
  return <Dialog title={t(`分配权限 · ${user.name}`, `Assign permissions · ${user.name}`)} onClose={onClose}>
    <PermissionEditor catalog={overview.permissions} templates={overview.permissionTemplates} selected={selected} scope={scope} onSelected={setSelected} onScope={setScope} />
    <div className="modal-actions"><button className="button secondary" onClick={onClose}>{t("取消", "Cancel")}</button><button className="button primary" disabled={busy || !selected.length} onClick={() => void save()}>{busy ? t("处理中…", "Processing…") : t("保存权限", "Save permissions")}</button></div>
  </Dialog>;
}

function ReviewRegistrationDialog({ request, overview, onClose, onSaved, onError }: { request: RegistrationRequestItem; overview: AdminOverview; onClose: () => void; onSaved: () => Promise<void>; onError: (value: string) => void }) {
  const { t } = useLanguage();
  const [decision, setDecision] = useState<"APPROVE" | "REJECT">("APPROVE");
  const [selected, setSelected] = useState<string[]>([]);
  const [scope, setScope] = useState<DirectDataScope>("SELF");
  const [busy, setBusy] = useState(false);
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (decision === "APPROVE" && !selected.length) return onError(t("请至少选择一项权限", "Select at least one permission"));
    setBusy(true);
    const data = new FormData(event.currentTarget);
    try {
      await api.reviewRegistration(request.id, { decision, permissions: decision === "APPROVE" ? selected : [], dataScope: decision === "APPROVE" ? scope : null, reason: String(data.get("reason")), expectedVersion: request.version });
      await onSaved();
    } catch (cause) {
      onError(cause instanceof Error ? cause.message : t("审核失败", "Review failed"));
    } finally {
      setBusy(false);
    }
  }
  return <Dialog title={t(`审核注册 · ${request.name}`, `Review registration · ${request.name}`)} onClose={onClose}>
    <form className="admin-form registration-review-form" onSubmit={submit}>
      <div className="full-field registration-summary"><strong>{request.username}</strong><span>{request.email}{request.mobile ? ` · ${request.mobile}` : ""}</span></div>
      <div className="full-field review-decision"><button type="button" className={decision === "APPROVE" ? "active" : ""} onClick={() => setDecision("APPROVE")}>{t("批准注册", "Approve")}</button><button type="button" className={decision === "REJECT" ? "active reject" : ""} onClick={() => setDecision("REJECT")}>{t("拒绝注册", "Reject")}</button></div>
      {decision === "APPROVE" && <div className="full-field"><PermissionEditor catalog={overview.permissions} templates={overview.permissionTemplates} selected={selected} scope={scope} onSelected={setSelected} onScope={setScope} /></div>}
      <label className="full-field">{decision === "APPROVE" ? t("审核备注（选填）", "Review notes (optional)") : t("拒绝原因", "Rejection reason")}<textarea name="reason" rows={4} required={decision === "REJECT"} minLength={decision === "REJECT" ? 2 : undefined} /></label>
      <div className="full-field registration-review-note">{t("批准后账号立即生效，并使用申请人设置的密码登录；权限与数据范围会写入审计日志。", "Approval activates the account with the applicant's password. Permissions and data scope are audited.")}</div>
      <Actions busy={busy} disabled={decision === "APPROVE" && !selected.length} onClose={onClose} text={decision === "APPROVE" ? t("批准并创建账号", "Approve and create account") : t("确认拒绝", "Confirm rejection")} />
    </form>
  </Dialog>;
}

function PermissionTemplateDialog({ source, overview, mode, onClose, onSaved, onError }: { source: PermissionTemplate | null; overview: AdminOverview; mode: "create" | "edit"; onClose: () => void; onSaved: () => Promise<void>; onError: (value: string) => void }) {
  const { t } = useLanguage();
  const [name, setName] = useState(mode === "create" && source ? `${source.name} - 副本` : source?.name ?? "");
  const [nameEn, setNameEn] = useState(mode === "create" && source ? `${source.nameEn || source.name} - Copy` : source?.nameEn ?? "");
  const [description, setDescription] = useState(source?.description ?? "");
  const [descriptionEn, setDescriptionEn] = useState(source?.descriptionEn ?? "");
  const [selected, setSelected] = useState(source?.permissions ?? []);
  const [scope, setScope] = useState<DirectDataScope>(source?.dataScope ?? "SELF");
  const [busy, setBusy] = useState(false);
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!selected.length) return onError(t("请至少选择一项权限", "Select at least one permission"));
    setBusy(true); onError("");
    const input: PermissionTemplateInput = { name, nameEn, description, descriptionEn, permissions: selected, dataScope: scope };
    try {
      if (mode === "edit" && source) await api.updatePermissionTemplate(source.id, input, source.version);
      else await api.createPermissionTemplate(input);
      await onSaved();
    } catch (cause) {
      onError(cause instanceof Error ? cause.message : t("权限模板保存失败", "Failed to save permission template"));
    } finally { setBusy(false); }
  }
  return <Dialog title={mode === "edit" ? t(`编辑权限模板 · ${source?.name}`, `Edit permission template · ${source?.nameEn || source?.name}`) : t("新建自定义权限模板", "New custom permission template")} onClose={onClose}>
    <form className="admin-form permission-template-form" onSubmit={submit}>
      <label>{t("模板名称", "Template name")}<input required minLength={2} maxLength={100} value={name} onChange={(event) => setName(event.target.value)} /></label>
      <label>{t("英文名称（选填）", "English name (optional)")}<input maxLength={100} value={nameEn} onChange={(event) => setNameEn(event.target.value)} /></label>
      <label className="full-field">{t("模板说明", "Description")}<textarea required minLength={2} maxLength={1000} rows={3} value={description} onChange={(event) => setDescription(event.target.value)} /></label>
      <label className="full-field">{t("英文说明（选填）", "English description (optional)")}<textarea maxLength={1000} rows={2} value={descriptionEn} onChange={(event) => setDescriptionEn(event.target.value)} /></label>
      <div className="full-field template-direct-note"><LayoutTemplate /><p>{source?.system && mode === "edit" ? t("这是系统预设模板：名称、说明、数据范围和权限组合均可编辑；模板代码固定，并且不能删除。修改不会自动改变已经分配给账号的权限。", "This is a system preset: its name, description, data scope, and permissions are editable; its code is fixed and it cannot be deleted. Changes do not update accounts already configured from it.") : t("模板只用于快速填入。给账号保存权限后，即使以后修改或删除模板，也不会自动改变该账号。", "Templates are prefills only. Editing or deleting a template later does not automatically change accounts already configured from it.")}</p></div>
      <div className="full-field"><PermissionEditor catalog={overview.permissions} selected={selected} scope={scope} onSelected={setSelected} onScope={setScope} /></div>
      <Actions busy={busy} disabled={!selected.length} onClose={onClose} text={mode === "edit" ? t("保存模板", "Save template") : t("创建模板", "Create template")} />
    </form>
  </Dialog>;
}

function ResetPasswordDialog({ user, onClose, onSaved, onError }: { user: ManagedUser; onClose: () => void; onSaved: () => Promise<void>; onError: (value: string) => void }) {
  const { t } = useLanguage();
  const [busy, setBusy] = useState(false);
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    const data = new FormData(event.currentTarget);
    try {
      await api.resetUserPassword(user.id, String(data.get("temporaryPassword")));
      await onSaved();
    } catch (cause) {
      onError(cause instanceof Error ? cause.message : t("重置失败", "Reset failed"));
    } finally {
      setBusy(false);
    }
  }
  return <Dialog title={t(`重置密码 · ${user.name}`, `Reset password · ${user.name}`)} onClose={onClose}>
    <form className="admin-form" onSubmit={submit}>
      <div className="reset-warning"><RotateCcwKey /><p>{t("重置后，该账号的所有登录会话立即失效，并要求使用临时密码重新登录和改密。", "Resetting invalidates all sessions and requires the temporary password for the next login and password change.")}</p></div>
      <PasswordField />
      <Actions busy={busy} onClose={onClose} text={t("确认重置", "Confirm reset")} />
    </form>
  </Dialog>;
}

function Actions({ busy, disabled = false, onClose, text }: { busy: boolean; disabled?: boolean; onClose: () => void; text: string }) {
  const { t } = useLanguage();
  return <div className="modal-actions"><button type="button" className="button secondary" onClick={onClose}>{t("取消", "Cancel")}</button><button className="button primary" disabled={busy || disabled}>{busy ? t("处理中…", "Processing…") : text}</button></div>;
}
